"use client";
import { STATS_API_URL } from "@/config/api";

import Image from "next/image";
import styles from "./info-tabs.module.scss";
import React, { useState } from "react";
import EcotopeCharts from "@/components/knowledge-hub/EcotopeCharts";

interface Props {
  handleLinkClick: (e: React.MouseEvent, id: string) => void;
}

const EcotopeAnalysisTab: React.FC<Props> = ({ handleLinkClick }) => {
  const [stats, setStats] = useState<any | null>(null);
  const [mosaicStats, setMosaicStats] = useState<any | null>(null);


  return (
    <div className={styles.tabContent}>
      <h2>Ecological Zones Analysis</h2>
      <p>
        When the <strong>All Ireland Raised Bog Ecotopes</strong> layer is selected, the
        platform classifies the area using ecological zones called <strong>ecotopes</strong>.
      </p>

      <video
        className={styles.image}
        autoPlay       
        muted         
        loop        
        playsInline     
        preload="metadata"
        poster="/info-page/mapecotope.png"
      >
        <source src="/info-page/ecotopemap.mp4" type="video/mp4" />
        {/* Optional second source for broader support
        <source src="/info-page/ecotopemap.webm" type="video/webm" /> */}
      </video>

      <p>
        The following analysis was generated from a user-selected box on the{" "}
        <strong>AI2Peat map</strong>. The ecotope distribution chart below shows the
        proportion of area covered by each ecotope type (in km²).
      </p>

      {/* 1) AREA chart (does the POST and fills `stats`) */}
      <EcotopeCharts
        endpoint={STATS_API_URL}
        payload={{
          layer_name: "All_Ireland_Ecotope",
          plot_type: "ecotope",
          polygon_coordinates: [
            { lat: 53.850299, lng: -8.484535 },
            { lat: 53.850299, lng: -8.407288 },
            { lat: 53.806125, lng: -8.407288 },
            { lat: 53.806125, lng: -8.484535 },
            { lat: 53.850299, lng: -8.484535 },
          ],
        }}
        render="area"
        onLoaded={(resp) => setStats(resp)}
        className={styles.image}
        height={360}
      />

      {/* spacing before the emissions section */}
      <div style={{ height: 12 }} />

      <h3>Carbon Emission of All Ireland Raised Bog</h3>
      <p>
        Based on known emission factors for each ecotope type, the platform estimates
        the total CO₂ impact.
      </p>

      {/* 2) EMISSIONS chart – render only after `stats` is set */}
      {stats ? (
        <EcotopeCharts
          initial={stats}
          render="emission"
          className={styles.image}
          height={360}
        />
      ) : (
        <div
          className={styles.image}
          style={{
            height: 360,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: "#fff",
            color: "#6b7280",
          }}
        >
          Loading chart…
        </div>
      )}

      <p>
        In this example, the area is dominated by <strong>Sub-marginal</strong> and{" "}
        <strong>Marginal</strong> ecotopes — both of which are associated with higher
        carbon emissions. The presence of these ecotopes can indicate a need for
        restoration.
      </p>

      {/* ---------- Mosaic section (live charts) ---------- */}
      <h2>Ecotope Mosaic Analysis</h2>
      <p>
        The <strong>Ecotope Mosaic</strong> layer provides enhanced classification of
        Ireland’s peatlands by integrating updated patch boundaries and transitional
        zones between ecotope types. This raster-based layer builds upon the original
        All Ireland Raised Bog Ecotopes by capturing finer-scale ecological variability.
      </p>

      <h3>Selected Area Insights</h3>
      <p>
        In the example shown below, a selected box area (displayed on the map) has been
        analyzed using the Mosaic layer. The chart illustrates the proportional
        coverage (in km²) of each ecotope type.
      </p>

      {/* ==== Mosaic: AREA (fetch + cache) ==== */}
      <EcotopeCharts
        endpoint={STATS_API_URL}
        payload={{
          layer_name: "ecotope_v2_mosaic",
          plot_type: "ecotope",
          // You can reuse POLY_BOX or supply a different box specific to your demo:
          polygon_coordinates: [
            { lat: 53.885928, lng: -8.783226 },
            { lat: 53.885928, lng: -8.717651 },
            { lat: 53.853539, lng: -8.717651 },
            { lat: 53.853539, lng: -8.783226 },
            { lat: 53.885928, lng: -8.783226 },
          ],
        }}
        render="area"
        onLoaded={(resp) => setMosaicStats(resp)}
        className={styles.image}
        height={360}
      />
      <div style={{ height: 12 }} />

      <h3>Carbon Emission Ecotope Mosaic</h3>
      <p>
        Based on emission factors for each ecotope, AI2Peat estimates the annual CO₂
        balance under three scenarios: Conservative, Average, and Non-conservative. The
        emission factors reflect different assumptions about peatland condition and
        vegetation productivity.
      </p>

      {/* ==== Mosaic: EMISSIONS (reuse cached response) ==== */}
      {mosaicStats ? (
        <EcotopeCharts initial={mosaicStats} render="emission" className={styles.image} height={360} />
      ) : (
        <div
          className={styles.image}
          style={{
            height: 360,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: "#fff",
            color: "#6b7280",
          }}
        >
          Loading chart…
        </div>
      )}

      <p>
        In this analysis, Sub-marginal and Sub-central ecotopes contribute the most to
        emissions. The <strong>Total</strong> emissions estimate varies based on the
        scenario: negative values indicate net sequestration, while positive values
        reflect carbon release.
      </p>

      <p>
        AI2Peat uses these factors to estimate the climate impact of selected regions
        and support science-based restoration planning and policy development.
      </p>

      <p className={styles.furtherReference}>
        <strong>Further references:</strong>{" "}
        For more information about ecotopes see the{" "}
        <a href="#ecotope-types" onClick={(e) => handleLinkClick(e, "ecotope-types")}>
          Ecotope Types
        </a>{" "}
        section.
      </p>
    </div>
  );
};

export default EcotopeAnalysisTab;